import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * Frontend-only admin gate. The real authorisation still happens server-side
 * (PLATFORM_ADMIN_UIDS env var + firestore.rules). This guard hits
 * /api/admin/check so that the client-side UID allowlist matches the sidebar
 * Admin link in LayoutV2 (single source of truth).
 */
export const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const authFetch = useAuthFetch();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading || !user) return;
    setChecking(true);
    authFetch('/api/admin/check')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled) setIsAdmin(Boolean(data?.isAdmin));
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, [authFetch, loading, user]);

  if (loading || (user && checking)) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #888)' }}>
        Loading…
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div style={{ padding: 32 }}>
        <h2>Access denied</h2>
        <p>This page is restricted to Rankify administrators.</p>
      </div>
    );
  }

  return <>{children}</>;
};
